import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { CrewContractColumn } from './useData';

export const moduleConfig = {
  module: {
    title: 'Wage columns',
    permissionModuleKey: 'crew_parameters',
    route: {
      list: '/parameters/crew?section=wage-columns',
      new: '/parameters/crew/wage-columns/new',
      detail: (id: number) => `/parameters/crew/wage-columns/${id}`,
      edit: (id: number) => `/parameters/crew/wage-columns/${id}/edit`},
    api: {
      list: '/crew-wage/columns',
      detail: (id: number) => `/crew-wage/columns/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by code or name…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['columnType', 'isActive']},
  ],
  filters: {
    fields: [
      {
        key: 'columnType',
        label: 'Type',
        placeholder: 'All types',
        getValue: (item: CrewContractColumn) => item.columnType,
        options: [
          { value: 'EARNING', label: 'Earning' },
          { value: 'DEDUCTION', label: 'Deduction' },
          { value: 'INFO', label: 'Info' },
        ]},
      {
        key: 'isActive',
        label: 'Active',
        type: 'boolean' as const,
        getValue: (item: CrewContractColumn) => (item.isActive ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ]},
    ],
    applyFilter: (items: CrewContractColumn[], filters: Record<string, string[]>) => {
      return items.filter((item) => {
        if (filters.columnType?.length && !filters.columnType.includes(item.columnType)) return false;
        if (filters.isActive?.length) {
          const s = item.isActive ? 'Yes' : 'No';
          if (!filters.isActive.includes(s)) return false;
        }
        return true;
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New wage column',
        showTitle: false,
        variant: 'primary' as const,
        href: '/parameters/crew/wage-columns/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: CrewContractColumn) => item.id,
  },
  emptyStates: {
    noData: 'Add a wage column (e.g. Basic, OT, Leave pay) to get started.',
    noSearchResults: 'No wage columns match your search.',
    noFilterResults: 'No wage columns match the filters.',
  },
  delete: {
    title: 'Delete wage column',
    message: 'Delete this wage column?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete wage column.',
    handler: async (id: number) => {
      const { deleteCrewWageColumn } = await import('../../../services/mutations');
      await deleteCrewWageColumn(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This wage column does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Wage column',
    getTitle: (item: CrewContractColumn) =>
      item.name ? item.name : `Wage column #${item.id}`},
  form: {
    new: {
      title: 'New wage column',
      subtitle: 'Define a contract wage component for scales and S/On',
      submitText: 'Create column'},
    edit: {
      title: 'Edit wage column',
      subtitle: 'Update how this component appears on wage scales and sign-on contracts',
      submitText: 'Save changes'},
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<CrewContractColumn>) => {
      const { createCrewWageColumn } = await import('../../../services/mutations');
      return createCrewWageColumn(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<CrewContractColumn>) => {
      const { updateCrewWageColumn } = await import('../../../services/mutations');
      return updateCrewWageColumn(id, data as Record<string, unknown>);
    }},
  fields: [
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'e.g. BASIC',
      validation: { required: true, minLength: 1, maxLength: 30 }},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Basic wage',
      validation: { required: true, minLength: 1, maxLength: 100 }},
    {
      name: 'columnType',
      label: 'Column type',
      type: 'select',
      required: true,
      options: [
        { value: 'EARNING', label: 'Earning' },
        { value: 'DEDUCTION', label: 'Deduction' },
        { value: 'INFO', label: 'Info' },
      ],
      defaultValue: 'EARNING'},
    {
      name: 'dataType',
      label: 'Data type',
      type: 'select',
      required: true,
      options: [
        { value: 'DECIMAL', label: 'Decimal' },
        { value: 'INTEGER', label: 'Integer' },
      ],
      defaultValue: 'DECIMAL'},
    {
      name: 'includeInTotal',
      label: 'Include in total',
      type: 'switch',
      switchLabel: 'Include in contract total',
      defaultValue: true},
    {
      name: 'sortOrder',
      label: 'Sort order',
      type: 'number',
      defaultValue: 0},
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Active',
      defaultValue: true},
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Optional notes',
      validation: { maxLength: 250 }},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'main',
        title: 'Wage column',
        icon: '',
        gridCols: 'md:grid-cols-12',
        fields: [
          'code',
          'name',
          'columnType',
          'dataType',
          'includeInTotal',
          'sortOrder',
          'isActive',
          'notes',
        ],
        fieldSpans: {
          code: 'md:col-span-3',
          name: 'md:col-span-9',
          columnType: 'md:col-span-4',
          dataType: 'md:col-span-4',
          includeInTotal: 'md:col-span-4',
          sortOrder: 'md:col-span-3',
          isActive: 'md:col-span-9',
          notes: 'md:col-span-12'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const COLUMN_TYPE_LABEL: Record<string, string> = {
  EARNING: 'Earning',
  DEDUCTION: 'Deduction',
  INFO: 'Info'};

export const getColumns = (formatDate: (dateString?: string) => string): Column<CrewContractColumn>[] => [
  { key: 'code', header: 'Code', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'columnType',
    header: 'Type',
    sortable: true,
    render: (v) => <TextCell value={COLUMN_TYPE_LABEL[String(v)] ?? String(v ?? '')} />},
  { key: 'dataType', header: 'Data', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'includeInTotal',
    header: 'In total',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.includeInTotal ? 'success' : 'secondary'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ?? row?.includeInTotal ? 'Yes' : 'No'}
      </Badge>
    )},
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.isActive ? 'success' : 'danger'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ?? row?.isActive ? 'Yes' : 'No'}
      </Badge>
    )},
  { key: 'sortOrder', header: 'Order', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (item: CrewContractColumn, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Code', value: item.code },
      { label: 'Name', value: item.name },
      { label: 'Column type', value: COLUMN_TYPE_LABEL[item.columnType] ?? item.columnType },
      { label: 'Data type', value: item.dataType },
      { label: 'Include in total', value: item.includeInTotal ? 'Yes' : 'No' },
      { label: 'Sort order', value: String(item.sortOrder) },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Notes', value: item.notes || '' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ]},
];

const TableActionsCell: React.FC<{
  item: CrewContractColumn;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: CrewContractColumn,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getStatusBadges = () => null;

export const getInitialFormData = (): Partial<CrewContractColumn> => ({
  code: '',
  name: '',
  columnType: 'EARNING',
  dataType: 'DECIMAL',
  includeInTotal: true,
  sortOrder: 0,
  isActive: true,
  notes: ''});

export const mapItemToFormData = (item: CrewContractColumn): Partial<CrewContractColumn> => ({
  code: item.code,
  name: item.name,
  columnType: item.columnType,
  dataType: item.dataType,
  includeInTotal: item.includeInTotal ?? true,
  sortOrder: item.sortOrder,
  isActive: item.isActive ?? true,
  notes: item.notes ?? ''});

export type ModuleConfig = typeof moduleConfig;
